import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, BACKEND_URL, useAuth } from "../../App";
import { descargarArchivo } from "../../utils/download";
import { whatsappUrl } from "../../lib/whatsapp";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Card, CardContent } from "../../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { 
  Plus, Search, FileSignature, CheckCircle, MessageCircle, Trash2, Edit,
  User, Download, PartyPopper, Eye, X
} from "lucide-react";
import PageHeader from "../../components/PageHeader";
import StatusBadge from "../../components/StatusBadge";

const Contratos = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "superadmin";
  const [contratos, setContratos] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [cotizaciones, setCotizaciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingContrato, setEditingContrato] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [empresaConfig, setEmpresaConfig] = useState(null);

  // Form - all editable fields
  const [formData, setFormData] = useState({
    cliente_id: "", cotizacion_id: "", servicios: [],
    total: 0, anticipo: 0, saldo: 0,
    fecha_anticipo: "", fecha_saldo: "", condiciones: ""
  });

  const fetchData = useCallback(async () => {
    try {
      const [contratosRes, clientesRes, cotizacionesRes, empresaRes] = await Promise.all([
        axios.get(`${API}/contratos`),
        axios.get(`${API}/clientes`),
        axios.get(`${API}/cotizaciones`),
        axios.get(`${API}/configuracion-empresa`)
      ]);
      setContratos(contratosRes.data);
      setClientes(clientesRes.data);
      setCotizaciones(cotizacionesRes.data);
      setEmpresaConfig(empresaRes.data);
    } catch {
      toast.error("Error al cargar contratos");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCotizacionChange = (cotId) => {
    const cot = cotizaciones.find(c => c.cotizacion_id === cotId);
    if (cot) {
      setFormData(prev => ({
        ...prev,
        cotizacion_id: cotId,
        cliente_id: cot.cliente_id,
        servicios: cot.servicios || [],
        total: cot.total,
        anticipo: cot.anticipo || 0,
        saldo: cot.saldo || (cot.total - (cot.anticipo || 0)),
      }));
    }
  };

  const handleAnticipoChange = (anticipo) => {
    const ant = parseFloat(anticipo) || 0;
    setFormData(prev => ({ ...prev, anticipo: ant, saldo: prev.total - ant }));
  };

  // --- Inline service editing ---
  const addServicio = () => {
    setFormData(prev => ({
      ...prev,
      servicios: [...prev.servicios, { nombre: "", cantidad: 1, precio: 0 }]
    }));
  };
  const updateServicio = (idx, field, value) => {
    setFormData(prev => {
      const servicios = prev.servicios.map((s, i) => i === idx ? { ...s, [field]: value } : s);
      const total = servicios.reduce((sum, s) => sum + (parseFloat(s.precio) || 0) * (parseInt(s.cantidad) || 1), 0);
      return { ...prev, servicios, total, saldo: total - prev.anticipo };
    });
  };
  const removeServicio = (idx) => {
    setFormData(prev => {
      const servicios = prev.servicios.filter((_, i) => i !== idx);
      const total = servicios.reduce((sum, s) => sum + (parseFloat(s.precio) || 0) * (parseInt(s.cantidad) || 1), 0);
      return { ...prev, servicios, total, saldo: total - prev.anticipo };
    });
  };

  const resetForm = () => {
    setEditingContrato(null);
    setFormData({
      cliente_id: "", cotizacion_id: "", servicios: [],
      total: 0, anticipo: 0, saldo: 0, fecha_anticipo: "", fecha_saldo: "", condiciones: ""
    });
  };

  const handleSave = async () => {
    if (!formData.cliente_id) {
      toast.error("Selecciona un cliente o cotizacion");
      return;
    }
    if (formData.servicios.length === 0) {
      toast.error("Agrega al menos un servicio");
      return;
    }
    try {
      if (editingContrato) {
        await axios.put(`${API}/contratos/${editingContrato.contrato_id}`, formData);
        toast.success("Contrato actualizado");
      } else {
        await axios.post(`${API}/contratos`, formData);
        toast.success("Contrato creado");
      }
      resetForm();
      await fetchData();
      setDialogOpen(false);
    } catch (error) {
      toast.error("Error al guardar el contrato");
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await axios.delete(`${API}/contratos/${deleteId}`);
      toast.success("Contrato eliminado");
      await fetchData();
    } catch (error) {
      toast.error("Error al eliminar");
    } finally {
      setDeleteId(null);
    }
  };

  const handleFirmar = async (contratoId) => {
    try {
      await axios.put(`${API}/contratos/${contratoId}`, { firmado: true });
      toast.success("Contrato marcado como firmado");
      await fetchData();
    } catch (error) {
      toast.error("Error al actualizar");
    }
  };

  const descargarPDF = async (cont) => {
    toast.info("Generando PDF...");
    try {
      const response = await axios.get(`${API}/contratos/${cont.contrato_id}/pdf`);
      descargarArchivo(response.data.url, response.data.filename);
      toast.success("PDF generado");
    } catch (error) {
      toast.error("Error al generar PDF");
    }
  };

  const handlePreview = async (cont) => {
    try {
      const res = await axios.get(`${API}/contratos/${cont.contrato_id}/preview`);
      setPreviewData(res.data);
    } catch {
      toast.error("Error al cargar preview");
    }
  };

  const crearEventoDesdeContrato = async (cont) => {
    try {
      const res = await axios.post(`${API}/contratos/${cont.contrato_id}/crear-evento`);
      toast.success("Evento creado desde contrato");
      navigate(`/panel/eventos/${res.data.evento_id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al crear evento");
    }
  };

  const enviarContratoPorWhatsApp = (cont) => {
    const cliente = clientes.find(c => c.cliente_id === cont.cliente_id);
    if (!cliente?.whatsapp) { toast.error("Cliente sin WhatsApp"); return; }
    let msg = `Hola ${cliente.nombre}, te envio los detalles de tu contrato:\n\n`;
    msg += `Evento: ${cliente.tipo_evento} - ${cliente.nombre_festejado}\n`;
    msg += `Total: $${(cont.total || 0).toLocaleString()} MXN\n`;
    msg += `Anticipo: $${(cont.anticipo || 0).toLocaleString()} MXN\n`;
    msg += `Saldo: $${(cont.saldo || 0).toLocaleString()} MXN\n\n`;
    if (!cont.firmado && cont.firma_token) {
      msg += `Firma tu contrato aqui:\n${window.location.origin}/firma/${cont.contrato_id}/${cont.firma_token}\n`;
    }
    const url = whatsappUrl(cliente.whatsapp, msg);
    if (!url) { toast.error("El WhatsApp del cliente no es un número válido"); return; }
    window.open(url, "_blank");
  };

  const openEditDialog = (cont) => {
    setEditingContrato(cont);
    setFormData({
      cliente_id: cont.cliente_id, cotizacion_id: cont.cotizacion_id || "",
      servicios: cont.servicios || [], total: cont.total || 0,
      anticipo: cont.anticipo || 0, saldo: cont.saldo || 0,
      fecha_anticipo: cont.fecha_anticipo || "", fecha_saldo: cont.fecha_saldo || "",
      condiciones: cont.condiciones || ""
    });
    setDialogOpen(true);
  };

  const filteredContratos = contratos.filter(cont => {
    if (!searchTerm) return true;
    const cliente = clientes.find(c => c.cliente_id === cont.cliente_id);
    const term = searchTerm.toLowerCase();
    return cliente?.nombre?.toLowerCase().includes(term) ||
           cont.cliente_nombre?.toLowerCase().includes(term) ||
           cont.contrato_id?.toLowerCase().includes(term);
  });
  const firmados = contratos.filter(c => c.firmado).length;
  const totalContratos = contratos.reduce((sum, c) => sum + (c.total || 0), 0);

  return (
    <div className="space-y-6" data-testid="contratos-page">
      <PageHeader
        title="Contratos"
        description="En esta pantalla generas el contrato a partir de una cotizacion aprobada. Al firmar (digital o en persona), se crea automaticamente el evento."
        primaryAction={{
          label: "Nuevo contrato",
          icon: Plus,
          testId: "btn-nuevo-contrato",
          onClick: () => { resetForm(); setDialogOpen(true); },
        }}
        secondaryActions={isAdmin ? [{
          label: "Descargar todos",
          icon: Download,
          testId: "btn-pdf-todos-contratos",
          onClick: () => window.open(`${BACKEND_URL}/api/admin/exportar/pdf/contratos/download`, "_blank"),
        }] : []}
      />

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="border-stone-200">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-10 h-10 bg-stone-100 rounded-full flex items-center justify-center">
              <FileSignature className="w-5 h-5 text-stone-600" />
            </div>
            <div>
              <p className="text-xs text-stone-500 uppercase tracking-wider">Total</p>
              <p className="font-display text-2xl text-stone-900">{contratos.length}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-stone-200">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-10 h-10 bg-rose-100 rounded-full flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-rose-600" />
            </div>
            <div>
              <p className="text-xs text-stone-500 uppercase tracking-wider">Firmados</p>
              <p className="font-display text-2xl text-stone-900">{firmados}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-stone-200 bg-rose-900">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-10 h-10 bg-rose-800 rounded-full flex items-center justify-center">
              <FileSignature className="w-5 h-5 text-rose-200" />
            </div>
            <div>
              <p className="text-xs text-rose-200 uppercase tracking-wider">Valor Total</p>
              <p className="font-display text-2xl text-white">${totalContratos.toLocaleString()} MXN</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Empresa config warning */}
      {empresaConfig && !empresaConfig.nombre_comercial && (
        <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 text-sm text-amber-800" data-testid="empresa-warning">
          Configura los datos de tu empresa en <button className="underline font-medium" onClick={() => navigate("/panel/configuracion")}>Configuracion</button> para que aparezcan en tus contratos PDF.
        </div>
      )}

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
        <Input placeholder="Buscar contrato..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10 border-stone-300" data-testid="search-contratos" />
      </div>

      {/* List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-rose-900 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : filteredContratos.length > 0 ? (
        <div className="space-y-4">
          {filteredContratos.map(cont => {
            const cliente = clientes.find(c => c.cliente_id === cont.cliente_id);
            return (
              <Card key={cont.contrato_id} className="border-stone-200 hover:shadow-sm transition-shadow" data-testid={`contrato-card-${cont.contrato_id}`}>
                <CardContent className="p-5">
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    <div className="flex items-start gap-3 min-w-0">
                      <div className={`w-10 h-10 shrink-0 rounded-full flex items-center justify-center ${cont.firmado ? 'bg-rose-100' : 'bg-amber-100'}`}>
                        <FileSignature className={`w-5 h-5 ${cont.firmado ? 'text-rose-600' : 'text-amber-600'}`} />
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <User className="w-3.5 h-3.5 text-stone-400 shrink-0" />
                          <p className="font-medium text-stone-900 truncate">{cliente?.nombre || cont.cliente_nombre || "Sin cliente"}</p>
                        </div>
                        <p className="text-sm text-stone-500 truncate">{cliente ? `${cliente.tipo_evento || ""} - ${cliente.nombre_festejado || ""}` : (cont.evento_nombre || "")}</p>
                        <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${cont.firmado ? 'bg-rose-100 text-rose-700' : 'bg-amber-100 text-amber-700'}`}>
                            {cont.firmado ? 'Firmado' : 'Pendiente'}
                          </span>
                          <span className="text-xs text-stone-400">{cont.servicios?.length || 0} servicios</span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-4 lg:gap-6 flex-wrap">
                      <div className="text-right">
                        <p className="text-[10px] text-stone-400 uppercase">Total</p>
                        <p className="font-display text-lg text-stone-900">${(cont.total || 0).toLocaleString()}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-[10px] text-stone-400 uppercase">Anticipo</p>
                        <p className="font-medium text-rose-600 text-sm">${(cont.anticipo || 0).toLocaleString()}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-[10px] text-stone-400 uppercase">Saldo</p>
                        <p className="font-medium text-amber-600 text-sm">${(cont.saldo || 0).toLocaleString()}</p>
                      </div>

                      <div className="flex items-center gap-1.5">
                        <Button variant="outline" size="sm" onClick={() => handlePreview(cont)} className="border-stone-200 h-8 w-8 p-0" title="Preview" data-testid={`btn-preview-${cont.contrato_id}`}>
                          <Eye className="w-3.5 h-3.5" />
                        </Button>
                        <a href={`${BACKEND_URL}/api/contratos/${cont.contrato_id}/pdf/download`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center rounded-md border border-stone-200 bg-white hover:bg-stone-50 h-8 w-8" title="PDF" data-testid={`btn-pdf-contrato-${cont.contrato_id}`}>
                          <Download className="w-3.5 h-3.5" />
                        </a>
                        <Button variant="outline" size="sm" onClick={() => openEditDialog(cont)} className="border-stone-200 h-8 w-8 p-0" title="Editar" data-testid={`btn-edit-${cont.contrato_id}`}>
                          <Edit className="w-3.5 h-3.5" />
                        </Button>
                        {!cont.firmado && (
                          <Button size="sm" onClick={() => handleFirmar(cont.contrato_id)} className="btn-primary text-xs h-8" data-testid={`btn-firmar-${cont.contrato_id}`}>
                            <CheckCircle className="w-3.5 h-3.5 mr-1" /> Firmar
                          </Button>
                        )}
                        {cliente && (
                          <Button variant="outline" size="sm" className="border-green-200 text-green-700 hover:bg-green-50 text-xs h-8" onClick={() => enviarContratoPorWhatsApp(cont)} data-testid={`btn-wa-${cont.contrato_id}`}>
                            <MessageCircle className="w-3.5 h-3.5 mr-1" /> WhatsApp
                          </Button>
                        )}
                        {cont.firmado && (
                          <Button variant="outline" size="sm" onClick={() => crearEventoDesdeContrato(cont)} className="border-rose-200 text-rose-700 hover:bg-rose-50 text-xs h-8" data-testid={`btn-crear-evento-${cont.contrato_id}`}>
                            <PartyPopper className="w-3.5 h-3.5 mr-1" /> Evento
                          </Button>
                        )}
                        <Button variant="outline" size="sm" onClick={() => setDeleteId(cont.contrato_id)} className="border-red-200 text-red-600 hover:bg-red-50 h-8 w-8 p-0">
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <div className="bg-white border border-stone-200 rounded-sm p-12 text-center">
          <FileSignature className="w-12 h-12 text-stone-300 mx-auto mb-3" />
          <h3 className="font-display text-lg text-stone-900 mb-1">No hay contratos</h3>
          <p className="text-stone-500 text-sm">Crea tu primer contrato desde una cotizacion</p>
        </div>
      )}

      {/* ==================== CREATE / EDIT DIALOG ==================== */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">
              {editingContrato ? "Editar Contrato" : "Nuevo Contrato"}
            </DialogTitle>
            <DialogDescription className="text-stone-500 text-sm">Configura los servicios y datos financieros del contrato</DialogDescription>
          </DialogHeader>
          <div className="space-y-5 py-4">
            {/* Step 1: Select source */}
            {!editingContrato && (
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label className="text-xs uppercase text-stone-500 tracking-wider">Crear desde cotizacion</Label>
                  <Select value={formData.cotizacion_id} onValueChange={handleCotizacionChange}>
                    <SelectTrigger className="border-stone-300" data-testid="select-cotizacion-contrato">
                      <SelectValue placeholder="Selecciona una cotizacion" />
                    </SelectTrigger>
                    <SelectContent>
                      {cotizaciones.map(cot => {
                        const cl = clientes.find(c => c.cliente_id === cot.cliente_id);
                        return (
                          <SelectItem key={cot.cotizacion_id} value={cot.cotizacion_id}>
                            {cl?.nombre} - {cl?.tipo_evento} - ${cot.total?.toLocaleString()}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
                {!formData.cotizacion_id && (
                  <div className="space-y-2">
                    <Label className="text-xs uppercase text-stone-500 tracking-wider">O selecciona un cliente</Label>
                    <Select value={formData.cliente_id} onValueChange={(v) => setFormData(prev => ({ ...prev, cliente_id: v }))}>
                      <SelectTrigger className="border-stone-300" data-testid="select-cliente-contrato">
                        <SelectValue placeholder="Selecciona un cliente" />
                      </SelectTrigger>
                      <SelectContent>
                        {clientes.map(cl => (
                          <SelectItem key={cl.cliente_id} value={cl.cliente_id}>{cl.nombre} - {cl.tipo_evento}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            )}

            {/* Client info (read-only summary) */}
            {formData.cliente_id && (() => {
              const cl = clientes.find(c => c.cliente_id === formData.cliente_id);
              if (!cl) return null;
              return (
                <div className="bg-stone-50 border border-stone-200 rounded-sm p-3 grid grid-cols-2 md:grid-cols-4 gap-2 text-sm" data-testid="contrato-cliente-info">
                  <div><span className="text-stone-400 text-xs">Cliente</span><p className="font-medium text-stone-900 truncate">{cl.nombre}</p></div>
                  <div><span className="text-stone-400 text-xs">Evento</span><p className="text-stone-700 truncate">{cl.tipo_evento}</p></div>
                  <div><span className="text-stone-400 text-xs">Fecha</span><p className="text-stone-700">{cl.fecha_evento || "Sin fecha"}</p></div>
                  <div><span className="text-stone-400 text-xs">Invitados</span><p className="text-stone-700">{cl.total_invitados || cl.num_invitados || "-"}</p></div>
                </div>
              );
            })()}

            {/* Servicios - INLINE EDITABLE */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-xs uppercase text-stone-500 tracking-wider">Servicios contratados</Label>
                <Button variant="outline" size="sm" onClick={addServicio} className="border-stone-300 text-xs h-7" data-testid="btn-add-servicio">
                  <Plus className="w-3 h-3 mr-1" /> Agregar
                </Button>
              </div>
              {formData.servicios.length === 0 ? (
                <p className="text-sm text-stone-400 italic py-3 text-center">Sin servicios. Agrega uno o importa desde cotizacion.</p>
              ) : (
                <>
                  {/* Iter124r · P1 · Desktop: grid. Móvil: cards apiladas para evitar overflow horizontal global */}
                  <div className="border border-stone-200 rounded-sm overflow-hidden hidden sm:block">
                    <div className="grid grid-cols-[1fr_70px_90px_90px_36px] gap-0 bg-stone-100 text-xs font-medium text-stone-500 uppercase tracking-wider">
                      <div className="px-3 py-2">Servicio</div>
                      <div className="px-2 py-2 text-center">Cant.</div>
                      <div className="px-2 py-2 text-right">P.U.</div>
                      <div className="px-2 py-2 text-right">Total</div>
                      <div></div>
                    </div>
                    {formData.servicios.map((s, idx) => (
                      <div key={`srv-${idx}`} className="grid grid-cols-[1fr_70px_90px_90px_36px] gap-0 border-t border-stone-100 items-center">
                        <Input value={s.nombre || ""} onChange={(e) => updateServicio(idx, "nombre", e.target.value)} placeholder="Nombre del servicio" className="border-0 rounded-none h-9 text-sm focus-visible:ring-0 focus-visible:ring-offset-0" data-testid={`srv-nombre-${idx}`} />
                        <Input type="number" value={s.cantidad ?? s.qty ?? ""} onChange={(e) => updateServicio(idx, "cantidad", parseInt(e.target.value) || 1)} className="border-0 border-l border-stone-100 rounded-none h-9 text-sm text-center focus-visible:ring-0 focus-visible:ring-offset-0" data-testid={`srv-cant-${idx}`} />
                        <Input type="number" value={s.precio_unitario ?? s.precio ?? ""} onChange={(e) => updateServicio(idx, "precio_unitario", parseFloat(e.target.value) || 0)} className="border-0 border-l border-stone-100 rounded-none h-9 text-sm text-right focus-visible:ring-0 focus-visible:ring-offset-0" data-testid={`srv-precio-${idx}`} />
                        <div className="px-2 text-sm font-medium text-stone-700 text-right border-l border-stone-100">
                          ${((parseFloat(s.precio_unitario ?? s.precio) || 0) * (parseInt(s.cantidad ?? s.qty) || 1)).toLocaleString()}
                        </div>
                        <button onClick={() => removeServicio(idx)} className="flex items-center justify-center h-9 text-stone-400 hover:text-red-500 transition-colors border-l border-stone-100" data-testid={`srv-remove-${idx}`}>
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                  <div className="sm:hidden space-y-2">
                    {formData.servicios.map((s, idx) => (
                      <div key={`srv-mob-${idx}`} className="border border-stone-200 rounded-lg p-3 bg-white" data-testid={`srv-card-mobile-${idx}`}>
                        <div className="flex items-start gap-2 mb-2">
                          <Input value={s.nombre || ""} onChange={(e) => updateServicio(idx, "nombre", e.target.value)} placeholder="Nombre del servicio" className="border-stone-200 text-sm flex-1" />
                          <button onClick={() => removeServicio(idx)} className="text-stone-400 hover:text-red-500 shrink-0 p-1"><X className="w-4 h-4" /></button>
                        </div>
                        {s.descripcion && <div className="text-xs text-stone-500 mb-2 break-words">{s.descripcion}</div>}
                        <div className="grid grid-cols-3 gap-2 text-xs">
                          <div>
                            <div className="text-stone-500 uppercase mb-0.5">Cant.</div>
                            <Input type="number" value={s.cantidad ?? s.qty ?? ""} onChange={(e) => updateServicio(idx, "cantidad", parseInt(e.target.value) || 1)} className="border-stone-200 text-sm h-9" />
                            {s.unidad && <div className="text-[10px] text-stone-400 mt-0.5">{s.unidad}</div>}
                          </div>
                          <div>
                            <div className="text-stone-500 uppercase mb-0.5">P.U.</div>
                            <Input type="number" value={s.precio_unitario ?? s.precio ?? ""} onChange={(e) => updateServicio(idx, "precio_unitario", parseFloat(e.target.value) || 0)} className="border-stone-200 text-sm h-9" />
                          </div>
                          <div>
                            <div className="text-stone-500 uppercase mb-0.5">Total</div>
                            <div className="px-2 py-2 rounded border border-stone-200 text-sm font-medium text-stone-700 text-right">${((parseFloat(s.precio_unitario ?? s.precio) || 0) * (parseInt(s.cantidad ?? s.qty) || 1)).toLocaleString()}</div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Financial */}
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs text-stone-500">Total</Label>
                <div className="bg-stone-50 border border-stone-200 rounded-sm px-3 py-2 font-display text-lg text-stone-900" data-testid="total-display">
                  ${formData.total.toLocaleString()} MXN
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-stone-500">Anticipo</Label>
                <Input type="number" value={formData.anticipo || ""} onChange={(e) => handleAnticipoChange(e.target.value)} className="border-stone-300" data-testid="input-anticipo-contrato" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-stone-500">Saldo</Label>
                <div className={`bg-stone-50 border border-stone-200 rounded-sm px-3 py-2 font-medium ${formData.saldo > 0 ? 'text-amber-600' : 'text-rose-600'}`}>
                  ${formData.saldo.toLocaleString()} MXN
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs text-stone-500">Fecha de anticipo</Label>
                <Input type="date" value={formData.fecha_anticipo} onChange={(e) => setFormData(prev => ({ ...prev, fecha_anticipo: e.target.value }))} className="border-stone-300" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-stone-500">Fecha de saldo</Label>
                <Input type="date" value={formData.fecha_saldo} onChange={(e) => setFormData(prev => ({ ...prev, fecha_saldo: e.target.value }))} className="border-stone-300" />
              </div>
            </div>

            {/* Empresa info (admin-only, read-only summary) */}
            {empresaConfig && empresaConfig.nombre_comercial && (
              <div className="bg-rose-50 border border-rose-200 rounded-sm p-3 text-sm space-y-1" data-testid="empresa-preview-contrato">
                <p className="text-xs text-rose-700 font-medium uppercase tracking-wider mb-1">Datos de empresa en PDF</p>
                <p className="text-rose-900 font-medium">{empresaConfig.nombre_comercial}</p>
                <p className="text-rose-800 text-xs">{empresaConfig.razon_social} | RFC: {empresaConfig.rfc}</p>
                <p className="text-rose-800 text-xs">{empresaConfig.locacion} - {empresaConfig.ubicacion} | Tel: {empresaConfig.telefono}</p>
                {isAdmin && (
                  <button className="text-xs text-rose-600 underline mt-1" onClick={() => navigate("/panel/configuracion")}>Editar datos de empresa</button>
                )}
              </div>
            )}

            {/* Condiciones extra (optional, per-contract) */}
            <div className="space-y-1.5">
              <Label className="text-xs text-stone-500">Notas adicionales del contrato (opcional)</Label>
              <Textarea value={formData.condiciones} onChange={(e) => setFormData(prev => ({ ...prev, condiciones: e.target.value }))} rows={2} placeholder="Notas adicionales para este contrato en particular..." className="border-stone-300 text-sm" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-stone-300">Cancelar</Button>
            <Button onClick={handleSave} className="btn-primary" data-testid="btn-guardar-contrato">
              {editingContrato ? "Actualizar" : "Crear"} Contrato
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ==================== PREVIEW DIALOG ==================== */}
      <Dialog open={!!previewData} onOpenChange={() => setPreviewData(null)}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Vista previa del contrato</DialogTitle>
            <DialogDescription className="text-stone-500 text-sm">Asi se veran los datos en el PDF final</DialogDescription>
          </DialogHeader>
          {previewData && (
            <div className="space-y-4 py-4 text-sm">
              {/* Empresa header */}
              {previewData.empresa && (
                <div className="text-center border-b border-stone-200 pb-3">
                  <p className="font-display text-lg text-rose-900">{previewData.empresa}</p>
                  <p className="text-xs text-stone-500">{previewData.razon_social}</p>
                  <p className="text-xs text-stone-400">RFC: {previewData.rfc} | Tel: {previewData.telefono_empresa}</p>
                  <p className="text-xs text-stone-400">{previewData.locacion} - {previewData.ubicacion_empresa}</p>
                </div>
              )}
              <p className="text-center font-medium text-stone-800 uppercase tracking-wider">Contrato de Prestacion de Servicios</p>

              {/* Cliente */}
              <div className="bg-stone-50 rounded-sm p-3">
                <p className="text-xs text-stone-500 font-medium uppercase mb-1">Datos del cliente</p>
                <div className="grid grid-cols-2 gap-1">
                  <p><span className="text-stone-400">Cliente:</span> <span className="font-medium">{previewData.cliente || "-"}</span></p>
                  <p><span className="text-stone-400">Telefono:</span> {previewData.telefono_cliente || "-"}</p>
                </div>
              </div>

              {/* Evento */}
              <div className="bg-stone-50 rounded-sm p-3">
                <p className="text-xs text-stone-500 font-medium uppercase mb-1">Datos del evento</p>
                <div className="grid grid-cols-2 gap-1">
                  <p><span className="text-stone-400">Tipo:</span> {previewData.tipo_evento || "-"}</p>
                  <p><span className="text-stone-400">Festejado:</span> {previewData.nombre_festejado || "-"}</p>
                  <p><span className="text-stone-400">Fecha:</span> {previewData.fecha_evento || "-"}</p>
                  <p><span className="text-stone-400">Horario:</span> {previewData.horario || "-"}</p>
                  <p><span className="text-stone-400">Lugar:</span> {previewData.ubicacion_evento || "-"}</p>
                  <p><span className="text-stone-400">Invitados:</span> {previewData.invitados || "-"}</p>
                </div>
              </div>

              {/* Servicios */}
              {previewData.servicios?.length > 0 && (
                <div>
                  <p className="text-xs text-stone-500 font-medium uppercase mb-2">Servicios contratados</p>
                  <div className="border border-stone-200 rounded-sm overflow-hidden text-xs">
                    <div className="grid grid-cols-[1fr_50px_70px_70px] bg-stone-100 font-medium text-stone-500 px-2 py-1.5">
                      <span>Servicio</span><span className="text-center">Cant.</span><span className="text-right">P.U.</span><span className="text-right">Total</span>
                    </div>
                    {previewData.servicios.map((s, i) => (
                      <div key={`prev-srv-${s.nombre}-${i}`} className="grid grid-cols-[1fr_50px_70px_70px] border-t border-stone-100 px-2 py-1.5">
                        <span className="truncate">{s.nombre}</span>
                        <span className="text-center">{s.cantidad}</span>
                        <span className="text-right">${(s.precio || 0).toLocaleString()}</span>
                        <span className="text-right font-medium">${(s.total || 0).toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Financial */}
              <div className="flex justify-end gap-6 text-right border-t border-stone-200 pt-3">
                <div><p className="text-stone-400 text-xs">Total</p><p className="font-display text-lg text-stone-900">${(previewData.total || 0).toLocaleString()} MXN</p></div>
                <div><p className="text-stone-400 text-xs">Anticipo</p><p className="font-medium text-rose-600">${(previewData.anticipo || 0).toLocaleString()}</p></div>
                <div><p className="text-stone-400 text-xs">Saldo</p><p className="font-medium text-amber-600">${(previewData.saldo || 0).toLocaleString()}</p></div>
              </div>

              {/* Responsables */}
              {(previewData.gerente || previewData.chef) && (
                <div className="text-xs text-stone-400 pt-2 border-t border-stone-100">
                  {previewData.gerente && <p>Gerente: {previewData.gerente}</p>}
                  {previewData.chef && <p>Chef: {previewData.chef}</p>}
                  {previewData.contacto_operativo_nombre && <p>Contacto: {previewData.contacto_operativo_nombre} - {previewData.contacto_operativo_rol}</p>}
                </div>
              )}

              <p className="text-xs text-stone-400 italic text-center">Vista previa. El PDF incluye terminos y condiciones, firmas y casilla de aceptacion.</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPreviewData(null)} className="border-stone-300">Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ==================== DELETE DIALOG ==================== */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar contrato?</AlertDialogTitle>
            <AlertDialogDescription>Esta accion no se puede deshacer.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700" data-testid="btn-confirmar-eliminar-contrato">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Contratos;
